import { useApolloClient } from "@apollo/client";
import { CardContainer, LabeledValue } from "@msys/ui";
import { Check as CheckIcon } from "@mui/icons-material";
import { Box, Button, Divider, Stack, Typography } from "@mui/material";
import { useTranslate } from "@tolgee/react";
import { Form, Formik } from "formik";
import React from "react";
import { useOutletContext } from "react-router-dom";
import {
  DecisionBehaviorOfSubitems,
  DocType,
} from "../../../../clients/graphqlTypes.js";
import { AutoSave } from "../../../commons/form-fields/AutoSave.js";
import { SelectField } from "../../../commons/form-fields/SelectField.js";
import { InputComponentProps } from "../../../trees/types.js";
import {
  createRecursiveTryFocusElementFunction,
  isInTestMode,
} from "../../../utils.js";
import { DecisionOptionListItem } from "../DecisionOptionListItem.js";
import {
  DecisionOptionsType,
  DecisionOptionsTypeTabs,
} from "../DecisionOptionsTypeTabs.js";
import { getDefaultDecisionQuestion } from "../helpers.js";
import { useDecisionItemMutations } from "../hooks/useDecisionItemMutations.js";
import { LabeledQuestionControlSection } from "../LabeledQuestionControlSection.js";
import {
  QuestionControlSection,
  QuestionControlSectionFormValues,
} from "../QuestionControlSection.js";
import {
  DecisionManagementBox_ChildItemFragment,
  DecisionManagementBox_ItemFragment,
  useDecisionManagementBox_ModifyWizardSettingsMutation,
} from "./DecisionManagementBox.generated.js";

interface Props<ChildItem extends DecisionManagementBox_ChildItemFragment> {
  projectId: string | null;
  doc: {
    id: string;
    docType: DocType;
    templateIsDraft: boolean;
    organisationId: string;
  };
  item: DecisionManagementBox_ItemFragment;
  onItemDeleted: () => void;
  isEditable?: boolean;
}

interface FormValues extends QuestionControlSectionFormValues {
  decisionBehaviorOfSubitems: DecisionBehaviorOfSubitems;
}

export const DecisionManagementBox = <
  ChildItem extends DecisionManagementBox_ChildItemFragment,
>({
  projectId,
  doc,
  item,
  onItemDeleted,
  isEditable = true,
}: Props<ChildItem>) => {
  const { t } = useTranslate(["Decisions", "OrganisationSettings"]);

  const { expandedItemIds, CreateChildComponent } = useOutletContext<{
    expandedItemIds: string[] | undefined;
    CreateChildComponent: React.ComponentType<
      InputComponentProps<
        DecisionManagementBox_ItemFragment,
        DecisionManagementBox_ChildItemFragment
      >
    >;
  }>();

  const client = useApolloClient();

  const [optionsType, setOptionsType] =
    React.useState<DecisionOptionsType>("all");

  const {
    modifyItemDecision,
    handleDecisionPreselectionChange,
    handleEliminateOption,
    handleResetEliminateOption,
    handleFinalizeSubitemDecision,
    canFinalizeSubitemDecision,
    isItemGoingToBeReplaced,
    modifyItemDecisionLoading,
    finalizeSubitemDecisionLoading,
    loading,
  } = useDecisionItemMutations<DecisionManagementBox_ItemFragment>(
    projectId,
    doc.id,
    isEditable,
    expandedItemIds
  );

  const onFinalizeClick = async () => {
    if (!item || !canFinalizeSubitemDecision(item)) return;

    const itemIsBeingReplaced = isItemGoingToBeReplaced(item);

    await handleFinalizeSubitemDecision(item.id);

    if (itemIsBeingReplaced) onItemDeleted();
  };

  const [modifyWizardSettings] =
    useDecisionManagementBox_ModifyWizardSettingsMutation({
      client,
    });

  const decisionBehaviourLabels = React.useMemo(
    (): Record<DecisionBehaviorOfSubitems, string> => ({
      NONE: t("NONE", {
        ns: "Decisions",
      }),
      SELECT_ONE: t("SELECT_ONE", {
        ns: "Decisions",
      }),
      SELECT_MANY: t("SELECT_MANY", {
        ns: "Decisions",
      }),
    }),
    [t]
  );

  const decisionTypeOptions = React.useMemo(
    (): {
      value: DecisionBehaviorOfSubitems;
      label: string;
    }[] => [
      ...(item?.decisionBehaviorOfSubitems === "NONE"
        ? [
            {
              value: "NONE",
              label: decisionBehaviourLabels.NONE,
            } as const,
          ]
        : []),
      {
        value: "SELECT_MANY",
        label: decisionBehaviourLabels.SELECT_MANY,
      } as const,
      {
        value: "SELECT_ONE",
        label: decisionBehaviourLabels.SELECT_ONE,
      } as const,
    ],
    [decisionBehaviourLabels, item?.decisionBehaviorOfSubitems]
  );

  if (!item) return <div>item not found</div>;

  const initialValues: FormValues = {
    decisionBehaviorOfSubitems: item.decisionBehaviorOfSubitems,
    questionControl: item.wizardSettings.decisionQuestionControl,
    // askWhen: item.wizardSettings.decisionQuestionControl.at(0)?.askWhen ?? [],
    // askWhom: item.wizardSettings.decisionQuestionControl.at(0)?.askWhom ?? [],
    prompt: item.wizardSettings.promptDecision,
  };

  const handleSubmit = async (values: FormValues) => {
    if (
      values.decisionBehaviorOfSubitems !==
      initialValues.decisionBehaviorOfSubitems
    ) {
      await modifyItemDecision({
        variables: {
          input: {
            projectId,
            docId: doc.id,
            itemId: item.id,
            values: {
              decisionSubitemsPreselection: [],
              decisionBehaviorOfSubitems: values.decisionBehaviorOfSubitems,
            },
          },
        },
      });
    }

    if (
      // values.askWhen !== initialValues.askWhen ||
      // values.askWhom !== initialValues.askWhom ||
      values.questionControl !== initialValues.questionControl ||
      values.prompt !== initialValues.prompt
    ) {
      await modifyWizardSettings({
        variables: {
          input: {
            projectId,
            docId: doc.id,
            itemId: item.id,
            values: {
              decisionQuestionControl: values.questionControl,
              // decisionQuestionControl: [
              //   { askWhen: values.askWhen, askWhom: values.askWhom },
              // ],
              promptDecision: values.prompt,
            },
          },
        },
      });
    }
  };

  const allAvailableItemChildren = item.children.filter(i => !i.deletedAt);
  const allItemChildren = allAvailableItemChildren.filter(
    i => !i.decisionOptionElimination
  );
  const eliminatedItemChildren = allAvailableItemChildren.filter(
    i => !!i.decisionOptionElimination
  );
  const selectedItemChildren = allAvailableItemChildren.filter(i =>
    item.decisionSubitemsPreselection.includes(i.id)
  );

  const itemChildren: DecisionManagementBox_ChildItemFragment[] = {
    all: allItemChildren,
    selected: selectedItemChildren,
    discarded: eliminatedItemChildren,
  }[optionsType];

  const noItemsMessage = {
    all: t("You have no options yet", {
      ns: "Decisions",
    }),
    selected: t("You have no selected options yet", {
      ns: "Decisions",
    }),
    discarded: t("You have no discarded options yet", {
      ns: "Decisions",
    }),
  }[optionsType];

  return (
    <CardContainer
      isExpandable
      title={t("Decision Request", {
        ns: "Decisions",
      })}
    >
      <Stack direction="column" p={1} spacing={1}>
        {isEditable ? (
          <Formik<FormValues>
            initialValues={initialValues}
            onSubmit={handleSubmit}
          >
            <Form>
              <Stack direction="column" spacing={1}>
                <SelectField
                  name="decisionBehaviorOfSubitems"
                  required
                  options={decisionTypeOptions}
                  label={t("Decision type", {
                    ns: "Decisions",
                  })}
                  disabled={modifyItemDecisionLoading}
                />
                <QuestionControlSection
                  isInitiallyExpanded
                  promptPlaceholder={getDefaultDecisionQuestion(t, item)}
                  disabled={false}
                />
                <AutoSave enableReinitialize initialValues={initialValues} />
              </Stack>
            </Form>
          </Formik>
        ) : (
          <>
            <LabeledValue label={t("Decision type", { ns: "Decisions" })}>
              {decisionBehaviourLabels[item.decisionBehaviorOfSubitems]}
            </LabeledValue>
            <LabeledValue label={t("Question", { ns: "Decisions" })}>
              {item.wizardSettings.promptDecision ||
                getDefaultDecisionQuestion(t, item)}
            </LabeledValue>
            <LabeledQuestionControlSection
              questionControl={item.wizardSettings.decisionQuestionControl}
              prompt={item.wizardSettings.promptDecision}
            />
          </>
        )}

        <Divider />

        <DecisionOptionsTypeTabs
          value={optionsType}
          onChange={setOptionsType}
        />

        {itemChildren.length > 0 ? (
          itemChildren.map(itemChild => (
            <DecisionOptionListItem
              key={itemChild.id}
              // layout={"vertical"}
              // parentItem={item}
              item={itemChild}
              parentItem={item}
              loading={loading}
              disabled={!isEditable}
              handleDecisionPreselectionChange={
                handleDecisionPreselectionChange
              }
              eliminateDecisionOption={handleEliminateOption}
              resetEliminateDecisionOption={handleResetEliminateOption}
            />
          ))
        ) : (
          <Box py={2} px={1}>
            <Typography variant="body2" color="gray" align="center">
              {noItemsMessage}
            </Typography>
          </Box>
        )}

        {isEditable && (
          <Box margin="-4px -4px -6px">
            <CreateChildComponent
              id={`msys-decision-input-${item.type}-${item.id}`}
              item={item}
              itemChildren={item.children}
              isRootItem={item.isRootItem}
              autoFocus={false}
              nodeRank={null}
              onAfterItemCreated={
                //  auto focus does not play nicely with e2e automated tests
                //  the e2e tests will set this sessionStorage as a temporary solution
                isInTestMode()
                  ? undefined
                  : createRecursiveTryFocusElementFunction(
                      `input#msys-decision-input-${item.type}-${item.id}:enabled`
                    )
              }
            />
          </Box>
        )}

        {isEditable &&
          ((item.decisionBehaviorOfSubitems === "SELECT_ONE" &&
            item.decisionSubitemsPreselection.length > 0) ||
            item.decisionBehaviorOfSubitems === "SELECT_MANY") && (
            <Button
              color="primary"
              variant="contained"
              size="medium"
              startIcon={<CheckIcon />}
              disabled={
                !isEditable || loading || !canFinalizeSubitemDecision(item)
              }
              loading={finalizeSubitemDecisionLoading}
              onClick={onFinalizeClick}
            >
              {t("Finalize decision", {
                ns: "Decisions",
              })}
            </Button>
          )}
      </Stack>
    </CardContainer>
  );
};
